import type { LanguageCode, SpeakRequest } from '@ampshift/shared';
import type { ApiClient } from '../api/client';
import { createStore, type Store } from '../store';

export interface SpeakerState {
  speaking: boolean;
  loading: boolean;
  error?: string;
}

export interface Speaker {
  readonly store: Store<SpeakerState>;
  /** Synthesises and plays; any current playback is stopped first. No-op when `enabled()` is false. */
  speak(text: string, language: LanguageCode, voiceId?: string): Promise<void>;
  stop(): void;
  dispose(): void;
}

export interface SpeakerDeps {
  api: Pick<ApiClient, 'speak'>;
  enabled: () => boolean;
  createAudio?: () => HTMLAudioElement;
}

/** Plays Polly mp3 through a single <audio> element. Audio bytes live only in the element's data URL. */
export function createSpeaker(deps: SpeakerDeps): Speaker {
  const store = createStore<SpeakerState>({ speaking: false, loading: false });
  let audio: HTMLAudioElement | undefined;
  let generation = 0;

  const stop = (): void => {
    generation += 1;
    const st = store.get();
    // Only touch the element when something is playing or loading: resetting an idle <audio> on iOS re-routes the
    // audio session and can mute a microphone capture that is starting at the same time.
    if (audio && (st.speaking || st.loading)) {
      try {
        audio.pause();
        audio.currentTime = 0;
      } catch {
        /* ignore */
      }
    }
    store.set({ speaking: false, loading: false });
  };

  const speak = async (text: string, language: LanguageCode, voiceId?: string): Promise<void> => {
    stop();
    if (!deps.enabled() || !text.trim()) return;
    const gen = generation;
    store.set({ speaking: false, loading: true });
    let res;
    try {
      const req: SpeakRequest = { text: text.slice(0, 1500), language, ...(voiceId ? { voiceId } : {}) };
      res = await deps.api.speak(req);
    } catch (err) {
      if (gen === generation) store.set({ speaking: false, loading: false, error: err instanceof Error ? err.message : String(err) });
      return;
    }
    if (gen !== generation || !deps.enabled()) return;
    audio ??= deps.createAudio ? deps.createAudio() : new Audio();
    const el = audio;
    el.src = `data:${res.contentType};base64,${res.audioBase64}`;
    el.onended = () => {
      if (gen === generation) store.set({ speaking: false, loading: false });
    };
    el.onerror = () => {
      if (gen === generation) store.set({ speaking: false, loading: false, error: 'Playback failed' });
    };
    try {
      store.set({ speaking: true, loading: false });
      await el.play();
    } catch (err) {
      if (gen === generation) store.set({ speaking: false, loading: false, error: err instanceof Error ? err.message : 'Playback blocked' });
    }
  };

  return {
    store,
    speak,
    stop,
    dispose: () => {
      stop();
      audio = undefined;
    },
  };
}

/** Picks the Polly language for a reply: Arabic script → the user's Arabic dialect, otherwise en-US. */
export function detectSpeechLanguage(text: string, arabicDialect: LanguageCode = 'ar-AE'): LanguageCode {
  const arabic = (text.match(/[؀-ۿ]/g) ?? []).length;
  const latin = (text.match(/[A-Za-z]/g) ?? []).length;
  return arabic > latin ? (arabicDialect.startsWith('ar') ? arabicDialect : 'ar-AE') : 'en-US';
}
